import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const BADGE_ICONS = ['ri-shield-star-line', 'ri-map-2-line', 'ri-star-line'];
const AVATAR_COLORS = ['JB', 'KP', 'RT'];

export default function Testimonials() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  const TESTIMONIALS = [1, 2, 3].map((n) => ({
    id: n,
    name: t(`testi.item${n}.name`),
    role: t(`testi.item${n}.role`),
    avatar: AVATAR_COLORS[n - 1],
    text: t(`testi.item${n}.text`),
    rating: 5,
    service: t(`testi.item${n}.service`),
  }));

  const BADGES = [1, 2, 3].map((n, i) => ({
    icon: BADGE_ICONS[i],
    label: t(`testi.badge${n}.label`),
    sub: t(`testi.badge${n}.sub`),
  }));

  return (
    <section id="testimonials" className="py-16 md:py-24 relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/452391e962488a6795318f5cacb41c00.jpg')" }}
      />
      {/* Strong overlay — guaranteed via inline style */}
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(240, 247, 250, 0.97)' }} />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center mb-10 md:mb-14">
          <span className="text-[#05294A] text-xs tracking-widest uppercase font-semibold">
            {t('testi.label')}
          </span>
          <h3 className="text-3xl md:text-5xl font-bold text-slate-900 mt-4">
            {t('testi.heading')}
          </h3>
          <p className="text-slate-500 mt-4 max-w-md mx-auto text-base px-4 md:px-0">
            {t('testi.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-start">
          <div className="bg-[#05294A] p-6 md:p-10 relative overflow-hidden">
            <div className="absolute top-6 right-8 text-[#0A3D6A]">
              <i className="ri-double-quotes-r text-5xl md:text-7xl" />
            </div>
            <div className="relative z-10">
              <div className="flex gap-1 mb-5 md:mb-6">
                {[...Array(TESTIMONIALS[active].rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-base" />
                ))}
              </div>
              <p className="text-white text-base md:text-lg leading-relaxed mb-6 md:mb-8 font-light">
                &ldquo;{TESTIMONIALS[active].text}&rdquo;
              </p>
              <div className="flex flex-wrap items-center gap-3 md:gap-4">
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-[#05294A] text-white font-bold text-sm shrink-0 border border-white/20">
                  {TESTIMONIALS[active].avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">{TESTIMONIALS[active].name}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{TESTIMONIALS[active].role}</p>
                </div>
                <div className="px-3 py-1 bg-[#67CBFF]/20 rounded-full border border-[#67CBFF]/30">
                  <span className="text-[#67CBFF] text-xs font-medium">{TESTIMONIALS[active].service}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 md:gap-4">
            {TESTIMONIALS.map((testi, idx) => (
              <button
                key={testi.id}
                onClick={() => setActive(idx)}
                className={`text-left p-4 md:p-5 border transition-all duration-300 cursor-pointer ${
                  active === idx
                    ? 'bg-[#05294A]/5 border-[#05294A]'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full font-bold text-xs shrink-0 ${
                    active === idx ? 'bg-[#05294A] text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {testi.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-slate-900 font-semibold text-sm">{testi.name}</p>
                      <div className="flex gap-0.5">
                        {[...Array(testi.rating)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-yellow-400 text-xs" />
                        ))}
                      </div>
                    </div>
                    <p className="text-slate-400 text-xs mb-2">{testi.role}</p>
                    <p className="text-slate-600 text-xs leading-relaxed line-clamp-2">{testi.text}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 md:mt-14 pt-8 md:pt-10 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {BADGES.map((badge) => (
            <div key={badge.label} className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#B8E2E8] text-[#05294A] shrink-0">
                <i className={`${badge.icon} text-lg md:text-xl`} />
              </div>
              <div>
                <p className="text-slate-800 font-semibold text-xs md:text-sm">{badge.label}</p>
                <p className="text-slate-400 text-xs mt-0.5">{badge.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
